import './free-result-reflections.js';
import { TAROT_DECK } from './three-card-deck.js';
import { secureRandomUnit, orientationFromUnit, validateQuestion, validateContext, SESSION_TTL_MS } from './three-card-flow.js';

export const STORAGE_KEY = 'totf-celtic-cross-v1';
export const DECK_PAGE_SIZE = 18;
export const READING_ORDER = Object.freeze([0, 1, 4, 5, 2, 6, 7, 8, 3, 9]);
const esc = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
const cardById = new Map(TAROT_DECK.map(card => [card.id, card]));

export function createSession(question, context = '', random = secureRandomUnit, now = Date.now()) {
  if (validateQuestion(question) || validateContext(context)) throw new Error('Invalid reading brief');
  const deck = [...TAROT_DECK];
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.min(i, Math.max(0, Math.floor(random() * (i + 1))));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return {
    version: 2, deckPage: 0, deckOffset: 0, id: globalThis.crypto?.randomUUID?.() || `${now}-${Math.floor(random()*1e9)}`,
    expiresAt: now + SESSION_TTL_MS, question: question.trim(), context,
    pool: deck.map(card => ({ id: card.id, orientation: orientationFromUnit(random()) })),
    chosen: [], selected: 0, visited: [], stage: 'draw', notes: {},
    reflection: { theme: '', influence: '', action: '' }, formDraft: null, completed: false
  };
}

export function placeCard(state, poolIndex) {
  if (state.chosen.length >= 10 || !Number.isInteger(poolIndex) || !state.pool[poolIndex] || state.chosen.includes(poolIndex)) return false;
  state.chosen.push(poolIndex);
  return true;
}

export function entryAt(state, position) {
  const entry = state.pool[state.chosen[position]];
  return entry ? { id: entry.id, orientation: position === 1 ? 'upright' : entry.orientation } : null;
}

export function restoreSession(raw, now = Date.now(), random = secureRandomUnit) {
  try {
    const s = JSON.parse(raw);
    if (!s || ![1, 2].includes(s.version) || typeof s.id !== 'string' || typeof s.question !== 'string' || typeof s.context !== 'string' || !Number.isFinite(s.expiresAt) || s.expiresAt <= now || s.expiresAt > now + SESSION_TTL_MS || validateQuestion(s.question) || validateContext(s.context)) return null;
    if (!Array.isArray(s.pool) || s.pool.length !== (s.version === 1 ? 10 : 78) || new Set(s.pool.map(c => c.id)).size !== s.pool.length || !s.pool.every(c => cardById.has(c.id) && ['upright', 'reversed'].includes(c.orientation))) return null;
    if (!Array.isArray(s.chosen) || s.chosen.length > 10 || new Set(s.chosen).size !== s.chosen.length || !s.chosen.every(i => Number.isInteger(i) && i >= 0 && i < s.pool.length)) return null;
    if (!['focus', 'draw', 'explore'].includes(s.stage) || s.stage === 'explore' && s.chosen.length !== 10 || !Number.isInteger(s.selected) || s.selected < 0 || s.selected > 9) return null;
    if (!Array.isArray(s.visited) || new Set(s.visited).size !== s.visited.length || !s.visited.every(i => Number.isInteger(i) && i >= 0 && i < s.chosen.length)) return null;
    if (!s.notes || typeof s.notes !== 'object' || Array.isArray(s.notes) || !Object.entries(s.notes).every(([k,v]) => /^[0-9]$/.test(k) && typeof v === 'string' && v.length <= 1500)) return null;
    if (s.formDraft !== null && (!s.formDraft || typeof s.formDraft.question !== 'string' || s.formDraft.question.length > 600 || typeof s.formDraft.context !== 'string' || s.formDraft.context.length > 1500)) return null;
    if (!s.reflection || !['theme','influence','action'].every(k => typeof s.reflection[k] === 'string' && s.reflection[k].length <= 1500) || typeof s.completed !== 'boolean' || s.completed && s.visited.length !== 10) return null;
    if (s.version === 1) {
      // Preserve every existing card, chosen index, note and original expiry.
      const ids = new Set(s.pool.map(card => card.id));
      const remaining = TAROT_DECK.filter(card => !ids.has(card.id));
      for (let i = remaining.length - 1; i > 0; i--) {
        const j = Math.min(i, Math.max(0, Math.floor(random() * (i + 1))));
        [remaining[i], remaining[j]] = [remaining[j], remaining[i]];
      }
      s.pool.push(...remaining.map(card => ({ id: card.id, orientation: orientationFromUnit(random()) })));
      s.version = 2; s.deckPage = 0;
    }
    if (!Number.isInteger(s.deckPage) || s.deckPage < 0 || s.deckPage >= Math.ceil(s.pool.length / DECK_PAGE_SIZE)) return null;
    // Older paged readings retain their cards and start near their former page.
    if (s.deckOffset === undefined) s.deckOffset = s.deckPage * DECK_PAGE_SIZE * 74;
    if (!Number.isFinite(s.deckOffset) || Math.abs(s.deckOffset) > 10000) return null;
    // A saved Safari rubber-band offset must not invalidate the reading.
    s.deckOffset = Math.max(0, s.deckOffset);
    return s;
  } catch { return null; }
}

class CelticCross {
  constructor(root) {
    this.root = root;
    this.abort = new AbortController();
    this.on = (node, type, fn) => node.addEventListener(type, fn, { signal: this.abort.signal });
    this.ui = JSON.parse(root.querySelector('[data-cc-strings]').textContent);
    this.images = JSON.parse(root.querySelector('[data-cc-images]').textContent);
    this.meanings = JSON.parse(root.querySelector('[data-cc-meanings]').textContent);
    if (TAROT_DECK.length !== 78 || !TAROT_DECK.every(c => this.images[c.id] && this.meanings[c.id]?.upright && this.meanings[c.id]?.reversed)) throw Error('Incomplete deck');
    this.form = root.querySelector('[data-focus-form]');
    this.question = this.form.elements.question;
    this.context = this.form.elements.context;
    this.slots = [...root.querySelectorAll('[data-position]')];
    this.status = root.querySelector('[data-session-status]');
    this.resetDialog = root.querySelector('[data-reset-dialog]');
    this.state = null;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      const stored = raw ? JSON.parse(raw) : null;
      if (stored?.draft === true && stored.expiresAt > Date.now() && stored.expiresAt <= Date.now() + SESSION_TTL_MS && typeof stored.question === 'string' && stored.question.length <= 600 && typeof stored.context === 'string' && stored.context.length <= 1500) {
        this.question.value = stored.question; this.context.value = stored.context; this.draftExpires = stored.expiresAt;
      } else if (raw) {
        this.state = restoreSession(raw);
        if (!this.state) { sessionStorage.removeItem(STORAGE_KEY); this.status.textContent = this.t('expired'); }
      }
    } catch { this.status.textContent = this.t('storage'); }
    if (this.state) { this.question.value = this.state.formDraft?.question ?? this.state.question; this.context.value = this.state.formDraft?.context ?? this.state.context; this.status.textContent = this.t('saved'); }
    this.bindDeckRail();
    this.on(this.form, 'submit', e => this.submit(e));
    this.on(root, 'click', e => this.click(e));
    this.on(root, 'change', e => { if (e.target.matches('[data-position-select]')) this.select(Number(e.target.value), true); });
    this.on(root, 'input', e => this.input(e));
    this.on(window, 'popstate', e => {
      if (!this.alive()) return;
      const h = e.state?.celticCross;
      const stage = h?.id === this.state?.id ? h.stage : 'focus';
      if (h?.id === this.state?.id && Number.isInteger(h.selected) && h.selected >= 0 && h.selected < 10) this.state.selected = h.selected;
      this.stage(stage, false, true);
    });
    this.on(window, 'pageshow', () => this.alive());
    this.on(window, 'scroll', () => this.scheduleClearance());
    this.on(window, 'resize', () => this.scheduleClearance());
    this.chatObserver = new MutationObserver(() => this.scheduleClearance());
    this.chatObserver.observe(document.body,{childList:true,subtree:true});
    this.stage(this.state?.stage || 'focus', false);
    this.history('replace');
    root.querySelector('[data-begin]').disabled = false;
    this.armExpiry();
  }
  bindDeckRail() {
    this.rail = this.root.querySelector('[data-fan]');
    this.on(this.rail, 'dragstart', e => e.preventDefault());
    this.on(this.rail, 'pointerdown', e => {
      if (!e.isPrimary || e.button !== 0 || this.currentStage !== 'draw') return;
      this.ignoreDeckClickUntil = 0;
      this.deckGesture = { id:e.pointerId, type:e.pointerType, x:e.clientX, y:e.clientY, left:this.rail.scrollLeft, moved:false, dragging:false };
    });
    this.on(window, 'pointermove', e => {
      const g=this.deckGesture;
      if (!g || g.id!==e.pointerId) return;
      const x=e.clientX-g.x, y=e.clientY-g.y;
      if (Math.hypot(x,y)>8) g.moved=true;
      // Touch stays native, including vertical page scrolling and inertia.
      if (g.type!=='mouse') return;
      if (!g.dragging && Math.abs(x)>8 && Math.abs(x)>Math.abs(y)) {
        g.dragging=true;this.rail.setPointerCapture(e.pointerId);this.rail.classList.add('is-dragging');
      }
      if (g.dragging) { if(e.cancelable)e.preventDefault();this.rail.scrollLeft=g.left-x; }
    });
    this.on(window, 'pointerup', e => this.endDeckGesture(e));
    this.on(window, 'pointercancel', e => this.endDeckGesture(e));
    this.on(window, 'blur', () => this.endDeckGesture());
    this.on(this.rail, 'lostpointercapture', e => this.endDeckGesture(e));
    this.on(this.rail, 'scroll', () => {
      if (!this.state || this.currentStage!=='draw' || this.state.chosen.length===10) return;
      this.state.deckOffset=this.deckOffset();
      this.updateDeckViewport();this.scheduleClearance();
      clearTimeout(this.deckSaveTimer);
      this.deckSaveTimer=setTimeout(()=>{if(this.state && this.alive())this.save();},120);
    });
    this.on(this.rail, 'focusin', e => {
      if (!e.target.matches('[data-pick]')) return;
      this.deckFocusIndex=Number(e.target.dataset.pick);
      this.rail.querySelectorAll('[data-pick]').forEach(b=>{b.tabIndex=b===e.target?0:-1;});
    });
    this.on(this.rail, 'keydown', e => {
      if (!['ArrowLeft','ArrowRight','Home','End'].includes(e.key) || !this.alive()) return;
      const buttons=[...this.rail.querySelectorAll('[data-pick]')];
      if(!buttons.length)return;
      e.preventDefault();
      const current=Math.max(0,buttons.indexOf(document.activeElement));
      const index=e.key==='Home'?0:e.key==='End'?buttons.length-1:Math.max(0,Math.min(buttons.length-1,current+(e.key==='ArrowRight'?1:-1)));
      const button=buttons[index];button.focus({preventScroll:true});
      const r=this.rail.getBoundingClientRect(),b=button.getBoundingClientRect();
      if(b.left<r.left+8)this.rail.scrollLeft+=b.left-r.left-8;
      else if(b.right>r.right-8)this.rail.scrollLeft+=b.right-r.right+8;
    });
    this.on(window, 'resize', () => this.updateDeckViewport());
    this.on(window, 'pagehide', () => { if(this.state)this.save(); });
    this.on(document, 'visibilitychange', () => { if(document.hidden && this.state)this.save(); });
  }
  endDeckGesture(event) {
    const g=this.deckGesture;
    if(!g || event && g.id!==event.pointerId)return;
    this.deckGesture=null;
    if(g.moved || event?.type==='pointercancel')this.ignoreDeckClickUntil=performance.now()+400;
    this.rail.classList.remove('is-dragging');
    if(this.rail.hasPointerCapture(g.id))this.rail.releasePointerCapture(g.id);
  }
  deckOffset() {
    // Safari can expose offsets outside the rail while its edges bounce.
    return Math.max(0,Math.min(this.rail.scrollWidth-this.rail.clientWidth,this.rail.scrollLeft));
  }
  updateDeckViewport() {
    if(!this.state || this.currentStage!=='draw' || this.state.chosen.length===10 || !this.rail.children.length)return;
    const first=this.rail.children[0],second=this.rail.children[1];
    const stride=second?second.offsetLeft-first.offsetLeft:first.offsetWidth;
    const offset=this.deckOffset();
    const from=Math.min(78,Math.floor(offset/stride)+1),to=Math.min(78,Math.ceil((offset+this.rail.clientWidth)/stride));
    this.root.querySelector('[data-deck-page]').textContent=this.t('deck_range',{from,to});
    const max=this.rail.scrollWidth-this.rail.clientWidth;
    this.root.querySelector('[data-deck-step="-1"]').disabled=offset<=1;
    this.root.querySelector('[data-deck-step="1"]').disabled=offset>=max-1;
  }
  scrollDeck(direction) {
    this.rail.scrollBy({left:direction*this.rail.clientWidth*.8,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});
  }

  t(key, values = {}) { return Object.entries(values).reduce((s,[k,v]) => s.replaceAll(`{${k}}`,String(v)), this.ui[key] ?? key); }
  name(i) { return this.t(`p${i+1}_name`); }
  save() {
    try {
      // Capture a just-finished pan before a pending scroll event or draw rebuild.
      if (this.state && this.currentStage==='draw' && this.state.chosen.length<10 && this.rail.clientWidth) this.state.deckOffset=this.deckOffset();
      if (this.state) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      else {
        this.draftExpires ||= Date.now() + SESSION_TTL_MS;
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ draft: true, expiresAt: this.draftExpires, question: this.question.value, context: this.context.value }));
      }
    } catch { this.status.textContent = this.t('storage'); }
  }
  armExpiry() {
    clearTimeout(this.expiryTimer);
    const expiry = this.state?.expiresAt || this.draftExpires;
    if (expiry) this.expiryTimer = setTimeout(() => this.alive(), Math.max(0, expiry - Date.now()) + 10);
  }
  alive() {
    const expiry = this.state?.expiresAt || this.draftExpires;
    if (!expiry || expiry > Date.now()) return true;
    this.clear(); this.status.textContent = this.t('expired'); return false;
  }
  history(mode) {
    try { history[`${mode}State`]({ ...history.state, celticCross: { id: this.state?.id || null, stage: this.currentStage, selected: this.state?.selected || 0 } }, ''); } catch {}
  }
  stage(next, push = true, focus = false) {
    if (next !== 'focus' && !this.state) next = 'focus';
    if (next === 'explore' && this.state.chosen.length !== 10) next = 'draw';
    this.currentStage = next;
    if (this.state) this.state.stage = next;
    this.root.dataset.stage = next;
    this.root.querySelector('[data-controls]').hidden = next === 'explore';
    this.form.hidden = next !== 'focus';
    this.root.querySelector('[data-draw-panel]').hidden = next !== 'draw';
    this.root.querySelector('[data-explore-panel]').hidden = next !== 'explore';
    this.root.querySelector('[data-session-actions]').hidden = !this.state;
    this.root.querySelectorAll('.cc-steps [data-stage-to]').forEach(b => {
      const value = b.dataset.stageTo;
      b.disabled = value === 'draw' && !this.state || value === 'explore' && this.state?.chosen.length !== 10;
      if (value === next) b.setAttribute('aria-current','step'); else b.removeAttribute('aria-current');
    });
    this.root.querySelector('[data-begin]').textContent = this.t(this.state ? 'save' : 'begin');
    this.root.querySelector('[data-edit-copy]').hidden = !this.state;
    this.root.querySelector('[data-cancel-edit]').hidden = !this.state;
    if (next === 'draw') this.renderDraw();
    if (next === 'explore') {
      this.renderReading();
      const slot=this.root.querySelector('.cc-report'),handoff=window.WotfReadingHandoff;
      if(slot&&handoff){
        const template=document.createElement('template');template.innerHTML=handoff.markup();
        const offer=template.content.firstElementChild;
        if(offer){
          slot.querySelectorAll('.cc-privacy').forEach(note=>offer.append(note.cloneNode(true)));
          handoff.mountOffer(this.root,slot,offer,'celtic-cross-reading');
        }
      }
    } else window.WotfReadingHandoff?.restoreOffer(this.root);
    this.renderSpread();
    this.renderAfter();
    if (this.state) this.save();
    if (push) this.history('push');
    this.scheduleClearance();
    if (focus) {
      const target = next === 'focus' ? this.question : this.root.querySelector(next === 'draw' ? '[data-draw-heading]' : '[data-reading-title]');
      target?.focus({ preventScroll: true });
    }
  }
  submit(event) {
    event.preventDefault();
    if (!this.alive()) return;
    const error = this.root.querySelector('#CCError');
    if (validateQuestion(this.question.value)) { error.textContent = this.t('question_error'); this.question.focus(); return; }
    if (validateContext(this.context.value)) { error.textContent = this.t('context_error'); this.context.focus(); return; }
    error.textContent = '';
    if (!this.state) this.state = createSession(this.question.value, this.context.value);
    else { this.state.question = this.question.value.trim(); this.state.context = this.context.value; this.state.formDraft = null; }
    this.armExpiry();
    this.stage(this.state.chosen.length === 10 ? 'explore' : 'draw', true, true);
  }
  input(e) {
    if (!this.alive()) return;
    if (e.target === this.question || e.target === this.context) {
      if (this.state) this.state.formDraft = {question:this.question.value, context:this.context.value};
      this.save(); this.armExpiry();
    }
    if (this.state && e.target.matches('[data-note]')) { this.state.notes[this.state.selected] = e.target.value.slice(0,1500); this.save(); }
    if (this.state && e.target.matches('[data-reflection]')) {
      this.state.reflection[e.target.dataset.reflection] = e.target.value.slice(0,1500); this.save();
    }
  }
  click(e) {
    if (e.target.closest('[data-reading-continue],[data-reading-download]')) {
      const offer = this.root.querySelector('[data-reading-offer]');
      if (!this.alive() || !this.state || this.currentStage !== 'explore' || this.state.chosen.length !== 10) { e.preventDefault(); e.stopPropagation(); return; }
      const handoff = window.WotfReadingHandoff;
      if (!handoff) {
        e.preventDefault(); e.stopPropagation();
        const error = offer.querySelector('[data-reading-transfer-error]');
        error.textContent = this.root.dataset.reportError; error.hidden = false; error.tabIndex = -1; error.focus();
        return;
      }
      const s = this.state;
      handoff.setData(this.root, {
        question: s.question, context: s.context,
        inputs: 'Reading format: Celtic Cross · 10 cards\nQuestion: ' + s.question + '\nContext: ' + s.context,
        result: s.chosen.map((_, i) => {
          const entry = entryAt(s, i), card = cardById.get(entry.id);
          const label = this.slots[i].querySelector('.cc-slot__name').textContent.trim();
          return 'Position ' + (i + 1) + ' · ' + label + '\n' + card.name + ' | ' + entry.orientation + ' | Card ID: ' + entry.id + (s.notes[i] ? '\nMy reflection: ' + s.notes[i] : '');
        }).join('\n\n'),
        notes: ['theme','influence','action'].map(k => s.reflection[k] ? this.t(k) + ': ' + s.reflection[k] : '').filter(Boolean).join('\n\n')
      });
    }

    const b = e.target.closest('button');
    if (!b || !this.root.contains(b) || b.disabled || !this.alive()) return;
    if (b.hasAttribute('data-stage-to')) this.stage(b.dataset.stageTo, true, true);
    if (b.hasAttribute('data-pick')) {
      if (e.detail && performance.now() < (this.ignoreDeckClickUntil || 0)) { e.preventDefault(); return; }
      this.pick(Number(b.dataset.pick));
    }
    if (b.hasAttribute('data-deck-step') && this.currentStage === 'draw' && this.state.chosen.length < 10) this.scrollDeck(Number(b.dataset.deckStep));
    if (b.hasAttribute('data-fill') && this.state) {
      const left = this.state.pool.map((_,i)=>i).filter(i=>!this.state.chosen.includes(i));
      left.slice(0,10-this.state.chosen.length).forEach(i=>placeCard(this.state,i)); this.save(); this.renderDraw(); this.renderSpread();
      this.root.querySelector('[data-explore]').focus({preventScroll:true});
    }
    if (b.hasAttribute('data-explore')) this.stage('explore', true, true);
    if (b.hasAttribute('data-image-retry')) this.renderSpread();
    if (b.hasAttribute('data-position') && this.currentStage === 'explore') this.select(Number(b.dataset.position), true);
    if (b.hasAttribute('data-position') && this.currentStage === 'focus') this.root.querySelector('#cc-positions').scrollIntoView({behavior:'auto',block:'start'});
    if (b.hasAttribute('data-reading-step')) {
      const n = READING_ORDER.indexOf(this.state.selected) + Number(b.dataset.readingStep);
      if (n >= 0 && n < 10) this.select(READING_ORDER[n], true);
    }
    if (b.hasAttribute('data-chapter')) this.select(Number(b.dataset.chapter), true);
    if (b.hasAttribute('data-finish')) {
      this.state.visited = [...new Set([...this.state.visited,this.state.selected])];
      if (this.state.visited.length < 10) this.select(READING_ORDER.find(i=>!this.state.visited.includes(i)), true);
      else { this.renderAfter(true); this.root.querySelector('[data-summary-title]').focus({preventScroll:true}); this.root.querySelector('[data-after]').scrollIntoView({block:'start',behavior:'auto'}); }
    }
    if (b.hasAttribute('data-complete')) {
      this.state.completed = true; this.save(); this.renderAfter(true);
      this.root.querySelector('[data-completion-status]').focus({preventScroll:true});
    }
    if (b.hasAttribute('data-review')) this.root.querySelector('[data-reading-title]')?.focus({preventScroll:false});
    if (b.hasAttribute('data-edit')) this.stage('focus', true, true);
    if (b.hasAttribute('data-cancel-edit')) {
      this.state.formDraft=null;this.question.value=this.state.question;this.context.value=this.state.context;
      this.stage(this.state.chosen.length===10 ? 'explore':'draw', true, true);
    }
    if (b.hasAttribute('data-restart')) this.resetDialog.showModal();
    if (b.hasAttribute('data-reset-cancel')) this.resetDialog.close();
    if (b.hasAttribute('data-reset-confirm')) this.clear();
  }
  pick(index) {
    if (this.pickLocked || this.currentStage !== 'draw') return;
    this.pickLocked = true;
    if (placeCard(this.state,index)) {
      this.save(); this.renderSpread(); this.renderDraw();
      this.root.querySelector('[data-draw-heading]').focus({preventScroll:true});
    }
    clearTimeout(this.pickTimer);
    this.pickTimer = setTimeout(()=>{this.pickLocked=false;},350);
  }
  renderDraw() {
    const n = this.state.chosen.length;
    this.root.querySelector('[data-draw-count]').textContent = n < 10 ? this.t('card_progress',{n:n+1}) : this.t('placed',{n});
    this.root.querySelector('[data-draw-heading]').textContent = this.t(n < 10 ? 'draw_heading' : 'ready');
    this.root.querySelector('[data-draw-copy]').textContent = n < 10 ? this.t('draw_copy',{n:n+1,name:this.name(n)}) : this.t('position_help');
    const offset=this.state.deckOffset, focus=this.deckFocusIndex ?? Math.floor(offset/74);
    const available=this.state.pool.map((_,i)=>i).filter(i=>!this.state.chosen.includes(i));
    const tabbable=available.find(i=>i>=focus) ?? available.at(-1);
    this.rail.innerHTML = n===10 ? '' : this.state.pool.map((_,i)=>{
      const chosen=this.state.chosen.indexOf(i);
      return chosen<0 ? `<button type="button" data-pick="${i}" tabindex="${i===tabbable?0:-1}" aria-label="${esc(this.t('choose'))} ${i+1}"><img src="${esc(this.images.back)}" alt="" width="400" height="600" decoding="async" draggable="false"></button>` : `<span class="cc-picked" role="img" aria-label="${esc(this.t('drawn_card',{n:chosen+1}))}"><span aria-hidden="true">${chosen+1}</span></span>`;
    }).join('');
    this.root.querySelector('[data-deck-controls]').hidden = n===10;
    this.root.querySelector('[data-deck-remaining]').textContent = this.t('deck_remaining',{n:this.state.pool.length-n});
    this.rail.scrollLeft=offset;this.updateDeckViewport();
    this.root.querySelector('[data-placed]').textContent = this.t('placed',{n});
    this.root.querySelector('[data-fill]').hidden = n === 10;
    this.root.querySelector('[data-explore]').hidden = n !== 10;
  }
  renderSpread() {
    this.slots.forEach((slot,i)=>{
      const entry = this.state && entryAt(this.state,i), shown = entry && this.currentStage !== 'focus';
      const img = slot.querySelector('img');
      slot.disabled = this.currentStage === 'draw' || !this.state && this.currentStage !== 'focus';
      slot.classList.toggle('is-placed',Boolean(shown));
      slot.classList.toggle('is-next',this.currentStage==='draw' && i===this.state.chosen.length);
      slot.setAttribute('aria-pressed',String(this.currentStage==='explore' && i===this.state.selected));
      slot.setAttribute('aria-label',`${i+1}. ${this.name(i)}${shown ? `: ${cardById.get(entry.id).name}, ${this.t(entry.orientation)}` : ''}`);
      const src = shown ? this.images[entry.id] : this.images.back;
      if (img.getAttribute('src') !== src) {
        img.src = src;
        if (shown) {
          const large = src.replace('-w400.webp','-w720.webp');
          img.srcset = `${src} 400w, ${large} 720w`; img.sizes = '(max-width: 600px) 64px, 88px';
        } else { img.removeAttribute('srcset'); img.removeAttribute('sizes'); }
      }
      img.classList.toggle('is-reversed',shown && entry.orientation==='reversed');
      img.alt = shown ? cardById.get(entry.id).name : '';
      img.onload = () => {
        if (shown && img.currentSrc !== this.images.back) slot.classList.remove('cc-image-error');
        if (!this.root.querySelector('.cc-image-error')) {
          this.root.querySelector('[data-image-retry]').hidden=true;
          if (this.status.textContent===this.t('image_error'))this.status.textContent='';
        }
      };
      img.onerror = () => {
        img.onerror=null; img.removeAttribute('srcset'); img.src=this.images.back; slot.classList.add('cc-image-error');
        this.status.textContent=this.t('image_error');
        this.root.querySelector('[data-image-retry]').hidden=false;
      };
    });
    this.root.querySelector('[data-spread-status]').textContent = this.state ? this.t('placed',{n:this.state.chosen.length}) : '';
  }
  select(i, focus=false) {
    if (this.currentStage !== 'explore' || !Number.isInteger(i) || i < 0 || i > 9) return;
    this.state.selected = i; this.renderReading(); this.renderSpread(); this.save(); this.history('replace');
    if (focus) this.root.querySelector('[data-reading-title]').focus({preventScroll:true});
  }
  renderReading() {
    const s=this.state,i=s.selected,entry=entryAt(s,i),card=cardById.get(entry.id),meaning=this.meanings[entry.id];
    if (!s.visited.includes(i)) s.visited.push(i);
    const step=READING_ORDER.indexOf(i), chapter=step<4?'heart':step<8?'influences':'way';
    const select=`<label class="cc-position-picker">${this.t('select_position')}<select data-position-select>${Array.from({length:10},(_,n)=>`<option value="${n}" ${n===i?'selected':''}>${n+1}. ${esc(this.name(n))}</option>`).join('')}</select></label>`;
    this.root.querySelector('[data-reading-panel]').innerHTML=`
      <nav class="cc-chapters" aria-label="Reading chapters">${[['heart',0],['influences',2],['way',3]].map(([key,n])=>`<button type="button" data-chapter="${n}" ${chapter===key?'aria-current="true"':''}>${esc(this.t('chapter_'+key))}</button>`).join('')}</nav>
      <p class="cc-chapter-copy">${esc(this.t('chapter_'+chapter+'_copy'))}</p>
      <section class="cc-reading" aria-labelledby="CCReadingTitle"><p class="cc-eyebrow">${esc(this.t('position_label',{n:i+1,name:this.name(i)}))}</p><h2 id="CCReadingTitle" tabindex="-1" data-reading-title>${esc(card.name)}</h2><p class="cc-orientation">${esc(this.t(entry.orientation))}</p><div class="cc-meaning"><h3>${esc(this.t('general'))}</h3><p>${esc(meaning[entry.orientation])}</p><h3>${esc(this.t('lens'))}</h3><p>${esc(this.t(`p${i+1}_prompt`))}</p></div><details class="cc-read-more"><summary>${esc(this.t('more'))}</summary><p>${esc(meaning.symbolism)}</p><h3>${esc(this.t('journal'))}</h3><p>${esc(meaning.practice)}</p><a href="${esc(this.root.dataset.root)}pages/tarot-card-meanings#lesson-${esc(entry.id)}">${esc(this.t('meaning_link'))}</a></details><label class="cc-note-label" for="CCNote">${esc(this.t('notes'))}</label><textarea id="CCNote" rows="2" maxlength="1500" data-note data-private="true" data-clarity-mask="true" placeholder="${esc(this.t('notes_placeholder'))}">${esc(s.notes[i]||'')}</textarea><div class="cc-reading__nav"><button class="cc-text" type="button" data-reading-step="-1" ${step===0?'disabled':''}>${esc(this.t('previous'))}</button><button class="cc-primary" type="button" ${step===9?'data-finish':'data-reading-step="1"'}>${esc(this.t(step===9?'finish':'next'))}</button></div><p class="cc-progress">${esc(this.t('explored',{n:s.visited.length}))}</p>${select}</section>`;
    this.save();
  }
  renderAfter(show=false) {
    const el=this.root.querySelector('[data-after]');
    if (!this.state || this.currentStage!=='explore' || this.state.visited.length!==10) { el.hidden=true; return; }
    if (!show && !this.state.completed && el.hidden) return;
    el.hidden=false;
    const s=this.state;
    el.innerHTML=`<section class="cc-synthesis"><h2 tabindex="-1" data-summary-title>${esc(this.t('framework'))}</h2><p>${esc(this.t('framework_copy'))}</p><div class="cc-reflections">${['theme','influence','action'].map(k=>`<label>${esc(this.t(k))}<textarea rows="3" maxlength="1500" data-reflection="${k}" data-private="true" data-clarity-mask="true">${esc(s.reflection[k])}</textarea></label>`).join('')}</div><button type="button" class="cc-primary" data-complete>${esc(this.t('complete'))}</button>${s.completed?`<p role="status" tabindex="-1" data-completion-status>${esc(this.t('completed'))}</p>`:''}<button type="button" class="cc-text" data-review>${esc(this.t('review'))}</button></section>`;
  }
  clear() {
    clearTimeout(this.expiryTimer); clearTimeout(this.pickTimer); clearTimeout(this.deckSaveTimer); this.endDeckGesture(); this.deckFocusIndex=null; this.pickLocked=false;
    if (this.resetDialog.open) this.resetDialog.close();
    this.state=null; this.draftExpires=null; this.form.reset();
    this.root.querySelector('[data-reading-panel]').replaceChildren();
    this.root.querySelector('[data-after]').replaceChildren();
    this.root.querySelector('[data-fan]').replaceChildren();
    this.status.textContent=''; this.root.querySelector('#CCError').textContent='';
    this.root.querySelector('[data-image-retry]').hidden=true;
    this.slots.forEach(slot=>slot.classList.remove('cc-image-error'));
    try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
    this.stage('focus',false,true); this.history('replace');
  }
  scheduleClearance() {
    if(this.clearanceFrame)return;
    this.clearanceFrame=requestAnimationFrame(()=>{
      this.clearanceFrame=0;
      const chat=document.querySelector('.fool-chat'),launcher=chat?.querySelector('[data-chat-toggle]'),panel=chat?.querySelector('#FoolShopChat');
      let collision=false;
      if(launcher && panel?.hidden){
        const r=launcher.getBoundingClientRect();
        collision=[...this.root.querySelectorAll('.cc-primary,.cc-slot,.cc-draw>.cc-text,[data-pick],[data-deck-step],textarea,select')].some(el=>{
          if(!el.getClientRects().length)return false;
          const b=el.getBoundingClientRect();
          return b.width>0&&b.height>0&&r.left<b.right+8&&r.right>b.left-8&&r.top<b.bottom+8&&r.bottom>b.top-8;
        });
      }
      this.root.toggleAttribute('data-chat-clearance',collision);
    });
  }
  dispose() { this.abort.abort(); this.chatObserver.disconnect(); cancelAnimationFrame(this.clearanceFrame); clearTimeout(this.expiryTimer); clearTimeout(this.pickTimer);clearTimeout(this.deckSaveTimer);this.endDeckGesture();this.root.removeAttribute('data-chat-clearance'); }
}

const instances = new WeakMap();
function mount(scope=document) {
  const roots=[...(scope.matches?.('[data-celtic-cross]')?[scope]:[]),...scope.querySelectorAll('[data-celtic-cross]')];
  roots.forEach(root=>{
    if (instances.has(root)) return;
    try { instances.set(root,new CelticCross(root)); }
    catch { const status=root.querySelector('[data-session-status]'); if(status)status.textContent='The reading could not start. Please reload this page. No cards have been drawn.'; }
  });
}
if (typeof document !== 'undefined') {
  mount();
  document.addEventListener('shopify:section:load',e=>mount(e.target));
  document.addEventListener('shopify:section:unload',e=>e.target.querySelectorAll('[data-celtic-cross]').forEach(root=>{instances.get(root)?.dispose();instances.delete(root);}));
}
